import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../contexts/AppContext";
import ClayCard, { BUTTON_COLORS, paletteFromColor } from "./ClayCard";
import { getButtonIcon } from "../lib/buttonIcons";
import PromoCard from "./PromoCard";
import { CARD2_DEFAULTS } from "./PromoCardEditor";
import CornerLaptop from "./CornerLaptop";
import Countdown from "./Countdown";

const sortByOrder = (a, b) => (a.order || 0) - (b.order || 0);

function badgeSizes(ring) {
  const on = ring && ring.enabled;
  const size = on ? Number(ring.size) || 40 : 28;
  const m = ring && ring.margin != null ? Math.max(0, Number(ring.margin)) : 10;
  const module = on ? Math.max(28, size) : 28;
  const iconCircle = on ? Math.max(22, Math.round(size * 0.66)) : 28;
  const glyph = Math.round(iconCircle * 0.55);
  const fontSize = on ? Math.max(14, Math.round(size * 0.34)) : 16;
  const padX = on ? (m + 4) : 16;
  const padY = on ? Math.max(4, m) : 6;
  return { on, module, iconCircle, glyph, fontSize, padX, padY };
}

function IconBadge({ Icon, ring, sz }) {
  if (!Icon) return null;
  const anim = ring && ring.anim ? ring.anim : "none";
  const bs = anim === "spin" ? "dashed" : "solid";
  return (
    <span className="relative inline-flex items-center justify-center shrink-0" style={{ width: `${sz.module}px`, height: `${sz.module}px` }}>
      {ring && ring.enabled && (
        <span className={`absolute rounded-full pointer-events-none ${anim !== "none" ? `ring-anim-${anim}` : ""}`} style={{
          width: `${ring.size}px`, height: `${ring.size}px`,
          left: "50%", top: "50%", transform: "translate(-50%, -50%)",
          border: `${ring.thickness}px ${bs} ${ring.color}`,
          opacity: Math.max(0, Math.min(100, Number(ring.opacity))) / 100,
          "--ring-color": ring.color,
        }} />
      )}
      <span className="btn-icon inline-flex items-center justify-center rounded-full" style={{ width: `${sz.iconCircle}px`, height: `${sz.iconCircle}px` }}>
        <Icon size={sz.glyph} strokeWidth={2.4} />
      </span>
    </span>
  );
}

function GroupBadge({ title, color, iconKey, align, format = "pill", effect = "none", opacity = 100, ring = null, tilt = 0, shadow = true }) {
  const c = paletteFromColor(color);
  const Icon = getButtonIcon(iconKey, null);
  const sz = badgeSizes(ring);
  const style = {
    "--btn-bg": c.bg, "--btn-dark": c.dark, "--btn-text": c.text,
    opacity: Math.max(0, Math.min(100, Number(opacity))) / 100,
    overflow: ring && ring.enabled ? "visible" : undefined,
    transform: tilt ? `rotate(${tilt}deg)` : undefined,
    paddingLeft: `${sz.padX}px`, paddingRight: `${sz.padX}px`,
    paddingTop: `${sz.padY}px`, paddingBottom: `${sz.padY}px`,
    boxShadow: shadow === false ? "none" : undefined,
  };
  return (
    <div className={`absolute -top-4 ${align === "right" ? "right-3" : "left-3"} z-10`} data-testid={`side-badge-${align}`}>
      <span className={`home-btn side-badge fmt-${format} fx-${effect} gap-2 font-fredoka font-bold`} style={style}>
        {align !== "right" && <IconBadge Icon={Icon} ring={ring} sz={sz} />}
        <span className="uppercase tracking-wide" style={{ fontSize: `${sz.fontSize}px` }}>{title}</span>
        {align === "right" && <IconBadge Icon={Icon} ring={ring} sz={sz} />}
      </span>
    </div>
  );
}

export default function SideButtons() {
  const navigate = useNavigate();
  const { lang, settings } = useApp();
  const [buttons, setButtons] = useState([]);

  useEffect(() => {
    api.get("/side-buttons").then((r) => setButtons(r.data || [])).catch(() => {});
  }, []);

  const go = (b) => {
    const url = (b.link_url || "").trim();
    if (!url) return;
    if (b.link_type === "external") window.open(url, "_blank", "noopener");
    else navigate(url.startsWith("/") ? url : `/${url}`);
  };

  const alunos = buttons.filter((b) => b.group === "alunos").sort(sortByOrder);
  const docentes = buttons.filter((b) => b.group === "docentes").sort(sortByOrder);
  const geral = buttons.filter((b) => b.group === "geral").sort(sortByOrder);
  const centro = buttons.filter((b) => b.group === "centro").sort(sortByOrder);
  if (buttons.length === 0) return null;

  const boxEnabled = settings.side_box_enabled !== false;
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const promo = settings.promo_card;
  const promoInline = promo && promo.enabled !== false && (promo.anchor || "below_alunos") === "below_alunos";
  const InlinePromo = promoInline ? <PromoCard promo={promo} lang={lang} backendUrl={backendUrl} inline /> : null;
  const promo2 = settings.promo_card_2;
  const InlinePromo2 = (promo2 && promo2.enabled) ? <PromoCard promo={{ ...CARD2_DEFAULTS, ...promo2 }} lang={lang} backendUrl={backendUrl} inline /> : null;
  const laptop = settings.corner_laptop;
  const laptopOn = laptop && laptop.enabled !== false;
  const InlineLaptop = laptopOn ? <CornerLaptop config={laptop} lang={lang} backendUrl={backendUrl} inline /> : null;
  const cdPos = settings.countdown_position;
  const cdOn = settings.countdown_enabled === true;
  const Cd = (pos, extra = "") => (cdOn && cdPos === pos)
    ? <div className={`flex justify-center mb-4 ${extra}`} data-testid={`countdown-${pos}`}><Countdown /></div>
    : null;
  const geralOn = settings.side_geral_enabled !== false && geral.length > 0;  const widthPct = settings.side_block_width_pct ?? 20;
  const alunosTop = settings.side_alunos_top_pct ?? 5;
  const alunosLeft = settings.side_alunos_left_pct ?? 3;
  const docTop = settings.side_docentes_top_pct ?? 5;
  const docRight = settings.side_docentes_right_pct ?? 3;
  const centroTop = settings.side_centro_top_pct ?? 42;
  const centroWidth = settings.side_centro_width_pct ?? 24;
  const boxStyle = {
    backgroundColor: hexWithOpacity(settings.side_box_color || "#FFFFFF", settings.side_box_opacity ?? 85),
  };

  const mkBtn = (extraStyle) => (b) => {
    const perBtn = {};
    if (Number(b.btn_width) > 0) perBtn.width = `${Number(b.btn_width)}px`;
    if (Number(b.btn_height) > 0) perBtn.minHeight = `${Number(b.btn_height)}px`;
    return (
    <ClayCard
      key={b.id}
      color={b.color}
      format={b.format}
      effect={b.effect}
      size={b.size || "sm"}
      titleSize={lang === "en" ? (b.title_size_en ?? b.title_size) : b.title_size}
      subtitleSize={b.subtitle_size}
      titleFont={b.title_font}
      subtitleFont={b.subtitle_font}
      icon={getButtonIcon(b.icon, null)}
      title={lang !== "en" ? b.label_pt : b.label_en}
      subtitle={lang !== "en" ? b.subtitle_pt : b.subtitle_en}
      onClick={() => go(b)}
      className={Number(b.btn_width) > 0 ? "" : "w-full"}
      style={{ ...(extraStyle || {}), ...perBtn }}
      testId={`side-btn-${b.id}`}
    />
    );
  };
  const renderBtn = mkBtn(undefined);

  const Block = ({ items, group, align }) => {
    if (items.length === 0) return null;
    const defTitle = group === "alunos" ? (lang !== "en" ? "Alunos" : "Students")
      : group === "docentes" ? (lang !== "en" ? "Docentes" : "Teachers")
      : group === "centro" ? (lang !== "en" ? "Destaque" : "Featured")
      : (lang !== "en" ? "Geral" : "General");
    const title = (lang !== "en" ? settings[`side_${group}_title_pt`] : settings[`side_${group}_title_en`]) || defTitle;
    const defColor = group === "alunos" ? "mix_green" : group === "docentes" ? "mix_purple" : group === "centro" ? "mix_gold" : "mix_blue";
    const badgeColor = settings[`side_${group}_badge_color`] || defColor;
    const badgeIcon = settings[`side_${group}_badge_icon`] || (group === "geral" || group === "centro" ? "sparkles" : "graduation");
    const badgeFormat = settings[`side_${group}_badge_format`] || "pill";
    const badgeEffect = settings[`side_${group}_badge_effect`] || "none";
    const badgeOpacity = settings[`side_${group}_badge_opacity`] ?? 100;
    const ring = {
      enabled: settings[`side_${group}_badge_ring_enabled`] === true,
      color: settings[`side_${group}_badge_ring_color`] || "#FFFFFF",
      thickness: settings[`side_${group}_badge_ring_thickness`] ?? 2,
      size: settings[`side_${group}_badge_ring_size`] ?? 40,
      opacity: settings[`side_${group}_badge_ring_opacity`] ?? 70,
      anim: settings[`side_${group}_badge_ring_anim`] || "none",
      margin: settings[`side_${group}_badge_ring_margin`] ?? 10,
    };
    const tilt = settings[`side_${group}_badge_tilt`] ?? 0;
    const badgeShadow = settings[`side_${group}_badge_shadow`] !== false;
    const badgeVisible = settings[`side_${group}_badge_visible`] !== false;
    const boxShadowOn = settings.side_box_shadow !== false;
    const grpBoxColor = settings[`side_${group}_box_color`] || settings.side_box_color || "#FFFFFF";
    const grpBoxOpacity = settings[`side_${group}_box_opacity`] ?? settings.side_box_opacity ?? 85;
    const grpBoxStyle = { backgroundColor: hexWithOpacity(grpBoxColor, grpBoxOpacity) };
    const sz = badgeSizes(ring);
    const badgeH = sz.module + 2 * sz.padY;
    const boxPadTop = badgeVisible ? Math.max(28, badgeH - 16 + 12) : 12;
    const cols = group === "geral" ? Math.max(1, Math.min(3, settings.side_geral_cols ?? 2)) : 1;
    const gW = group === "geral" ? (Number(settings.side_geral_btn_width) || 0) : 0;
    const gH = group === "geral" ? (Number(settings.side_geral_btn_height) || 0) : 0;
    const geralBtn = mkBtn(gH ? { minHeight: `${gH}px` } : undefined);
    let content;
    if (group === "geral") {
      const colTpl = gW > 0 ? `${gW}px` : "minmax(0, 1fr)";
      content = <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${cols}, ${colTpl})` }}>{items.map(geralBtn)}</div>;
    } else {
      content = <div className="flex flex-col gap-3">{items.map(renderBtn)}</div>;
    }
    // When laying buttons side by side with a fixed width, let the block grow
    // beyond the (narrow) column so they actually sit next to each other.
    const blockStyle = group === "geral" && (cols > 1 || gW > 0) ? { width: "max-content", maxWidth: "92vw" } : undefined;
    return (
      <div className="relative" style={blockStyle} data-testid={`side-block-${group}`}>
        {boxEnabled && badgeVisible && <GroupBadge title={title} color={badgeColor} iconKey={badgeIcon} align={align} format={badgeFormat} effect={badgeEffect} opacity={badgeOpacity} ring={ring} tilt={tilt} shadow={badgeShadow} />}
        <div
          className={boxEnabled ? `rounded-3xl ${boxShadowOn ? "shadow-xl" : ""} backdrop-blur-sm border border-white/40 px-3 pb-3` : ""}
          style={boxEnabled ? { ...grpBoxStyle, paddingTop: `${boxPadTop}px` } : undefined}
        >
          {content}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Desktop: floating boxes positioned over the background panels (% of full-width area) */}
      <div className="hidden lg:block absolute z-20" style={{ top: `${alunosTop}%`, left: `${alunosLeft}%`, width: `${widthPct}%` }} data-testid="side-col-alunos">
        {Cd("above_alunos")}
        <Block items={alunos} group="alunos" align="left" />
        {geralOn && <div className="mt-6"><Block items={geral} group="geral" align="left" /></div>}
        {Cd("above_loja")}
        {InlinePromo}
      </div>
      <div className="hidden lg:block absolute z-20" style={{ top: `${docTop}%`, right: `${docRight}%`, width: `${widthPct}%` }}>
        {Cd("above_docentes")}
        <Block items={docentes} group="docentes" align="right" />
        {Cd("above_laptop")}
        {InlineLaptop}
      </div>
      {centro.length > 0 && (
        <div className="hidden lg:block absolute z-20" style={{ top: `${centroTop}%`, left: "50%", transform: "translateX(-50%)", width: `${centroWidth}%` }} data-testid="side-col-centro">
          <Block items={centro} group="centro" align="left" />
        </div>
      )}

      {/* Mobile/tablet: stacked below content. Order: alunos, geral, docentes, laptop, loja, caixa inferior */}
      <div className="lg:hidden mt-2 space-y-8 px-4 sm:px-6 max-w-2xl mx-auto" data-testid="side-buttons-mobile">
        {Cd("above_alunos")}
        <Block items={alunos} group="alunos" align="left" />
        {geralOn && <Block items={geral} group="geral" align="left" />}
        {Cd("above_docentes")}
        <Block items={docentes} group="docentes" align="left" />
        {centro.length > 0 && <Block items={centro} group="centro" align="left" />}
        {Cd("above_laptop")}
        {laptopOn && laptop.hide_mobile !== true && InlineLaptop}
        {Cd("above_loja")}
        {InlinePromo && <div className="flex justify-center [&>div]:!ml-0">{InlinePromo}</div>}
        {Cd("above_bottom_box")}
        {InlinePromo2 && <div className="flex justify-center [&>div]:!ml-0 pb-20">{InlinePromo2}</div>}
      </div>
    </>
  );
}

function hexWithOpacity(hex, opacityPct) {
  const o = Math.max(0, Math.min(100, Number(opacityPct))) / 100;
  let h = (hex || "#FFFFFF").replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16) || 255;
  const g = parseInt(h.slice(2, 4), 16) || 255;
  const b = parseInt(h.slice(4, 6), 16) || 255;
  return `rgba(${r}, ${g}, ${b}, ${o})`;
}
